import { useStore } from '../../contexts/store'
import { observer } from 'mobx-react-lite'
import { getStroke } from 'perfect-freehand'
import { JSX, useRef } from 'react'
import { rectsFromRange, getSelectionRange } from './PdfClickEvents'
import { PencilCoordinate } from '../../stores/uiStore'
import CloseIcon from '@mui/icons-material/Close';
import { SelectionTool } from '../../shared/src/types/Session'

const average = (a:number, b:number) => (a + b) / 2

function getSvgPathFromStroke(points:any, closed = true) {
  const len = points.length

  if (len < 4) {
    return ``
  }

  let a = points[0]
  let b = points[1]
  const c = points[2]

  let result = `M${a[0].toFixed(2)},${a[1].toFixed(2)} Q${b[0].toFixed(
    2
  )},${b[1].toFixed(2)} ${average(b[0], c[0]).toFixed(2)},${average(
    b[1],
    c[1]
  ).toFixed(2)} T`

  for (let i = 2, max = len - 1; i < max; i++) {
    a = points[i]
    b = points[i + 1]
    result += `${average(a[0], b[0]).toFixed(2)},${average(a[1], b[1]).toFixed(
      2
    )} `
  }

  if (closed) {
    result += 'Z'
  }

  return result
}

export const getSvgFromCoordinates = (data:PencilCoordinate[], scale:number, tool:SelectionTool) => {
  const stroke = data.length ? getStroke(data.map(a => ({x: a.x, y: a.y, pressure: a.t} )), {
    size: (tool.radius || 5) * scale,
    thinning: 0.5,
    smoothing: 0.5,
    streamline: 0.5,
    simulatePressure: data[0].t > 1000000 ? true : false,
    start: {
      taper: 0,
      easing: (t) => t,
      cap: true
    },
    end: {
      taper: 0,
      easing: (t) => t,
      cap: true
    }
  }) : getStroke([], {
    size: 16,
    thinning: 0.5,
    smoothing: 0.5,
    streamline: 0.5,
  })
  return getSvgPathFromStroke(stroke)
}

const AnnotationInProgress = ({scale, storeId}:{scale:number, storeId:string}) => {
  const { uiStore, pdfMetaStore, sessionStore } = useStore()
  const canvasRef = useRef<null|HTMLDivElement>(null)
  const tool = sessionStore.getSelectionTool()
  if (!tool) return null
  const color = uiStore.getSemiTransparentColor(tool.color)

  var rect:DOMRect|null = null
  if (canvasRef && canvasRef.current) {
    rect = canvasRef.current.getBoundingClientRect()
  }

  const overlay = (overlayContent:JSX.Element|JSX.Element[]|null = null) => {
    return <div ref={canvasRef} className={"freehand-canvas freehand-canvas-" + tool.type} style={{
      position:'absolute',
      top:0,
      left:0,
      bottom:0,
      right:0,
      backgroundColor:'#fff8',
      userSelect:'none',
    }}>
      <div style={{position:'absolute', left:'50%', marginLeft:-100, top:5, height:22, width: 2*uiStore.contiguousCountdown, overflow:'hidden', whiteSpace:'nowrap' , fontSize:11, verticalAlign:'top', backgroundColor:'#ccc', color:'#444'}} >
        <div style={{display:'inline-block', height:20, verticalAlign:'top'}}><CloseIcon /></div>
        <div style={{display:'inline-block', height:20, verticalAlign:'top',lineHeight:'22px'}}>Annotation in Progress</div>
      </div>
      {overlayContent}
    </div>
  }

  if (uiStore.annotationInProgress) {
    switch(tool.type) {

      case 'marker':
        if (uiStore.selectionStartRange && uiStore.selectionEndRange) {
          const scale = pdfMetaStore.getScale(storeId)
          const range = getSelectionRange(uiStore.selectionStartRange, uiStore.selectionEndRange)
          const rects = rectsFromRange(range, scale, true)
          const divs = rects.map((r, i) => {
            return <div key={i} style={{
              position:'absolute',
              backgroundColor:color,
              left: scale * r.x - (rect?.x || 0),
              top: scale * r.y - (rect?.y || 0),
              width: scale * r.w,
              height:  scale * r.h,
              userSelect:'none'
            }}></div>})
          return overlay(divs)

        }
        break

      case 'box':
        if (uiStore.annotationInProgressData) return overlay(<div style={{
          position:'absolute',
          left: Math.min(uiStore.annotationInProgressData.startX, uiStore.annotationInProgressData.endX) - (rect?.x || 0),
          top: Math.min(uiStore.annotationInProgressData.startY, uiStore.annotationInProgressData.endY) - (rect?.y || 0),
          width: Math.abs(uiStore.annotationInProgressData.endX - uiStore.annotationInProgressData.startX),
          height: Math.abs(uiStore.annotationInProgressData.endY - uiStore.annotationInProgressData.startY),
          backgroundColor:color,
        }}></div>)
        break

      case 'pencil':
      case 'highlighter':
        if (uiStore.pencilData.coordinates.length>1) {
          var coordinates:PencilCoordinate[] = []
          if ((rect !== null) && (rect.x || rect.y)) {
            coordinates = uiStore.pencilData.coordinates.map(r => ({ x: r.x - (rect?.x || 0), y: r.y - (rect?.y || 0), t: r.t }))
          }
          else {
            coordinates = uiStore.pencilData.coordinates
          }

          return overlay(<svg style={{position:'absolute', zIndex:1, top:0, width:'100%', height:'100%'}}>
            <path fill={color} stroke={'#fff0'} d={getSvgFromCoordinates(coordinates, scale, tool)} />
          </svg>)
        }
        break
    }
    return overlay(null)
  }

  return null
}

export default observer(AnnotationInProgress)